import {
  COPY_BATCH_DIALOG_LEGEND_ITEMS as legendItems,
  COPY_BATCH_TEMPLATE_VERSION_LABEL,
} from "../../../constants/investigationAnalysisReview";

import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MetadataChangesPopover from "./MetadataChangesPopover";
import DerivedParameterChanges from "./DerivedParameterChanges";
import { COPY_BATCH_PARAMETER_TYPE_CHANGE_MESSAGE } from "../../../constants/parameterMessages";

const stateStyles = {
  retained: {
    color: "#101828",
    background: "#FFFFFF",
  },
  added: {
    color: "#14804A",
    background: "#EAF7E8",
  },
  kept: {
    color: "#101828",
    background: "#C4C1C1",
  },
  removed: {
    color: "#667085",
    background: "#F2F4F7",
  },
  moved: {
    color: "#2563EB",
    background: "#EAF2FF",
  },
  metadata_changed: {
    color: "#854D0E",
    background: "#FFF4D6",
  },
};

const cellSx = {
  border: "1px solid #DDE3EA",
  px: 1.25,
  py: 1,
  textAlign: "center",
  fontSize: 14,
  whiteSpace: "nowrap",
};

const messages = [
  {
    severity: "success",
    content: (
      <>
        <strong>Tabs and parameters that exist in both templates</strong> retain
        their existing data.
      </>
    ),
  },
  {
    severity: "info",
    content: (
      <>
        <strong>New tabs and parameters</strong> are added with blank data.
      </>
    ),
  },
  {
    severity: "warning",
    content: (
      <>
        <strong>Parameters missing from the new template</strong> are retained
        when old data is present and marked separately.
      </>
    ),
  },
  {
    severity: "info",
    content: COPY_BATCH_PARAMETER_TYPE_CHANGE_MESSAGE,
  },
  {
    severity: "warning",
    content:
      "Please review the new batch after copying to ensure all data is as expected.",
  },
];

const normalizeStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const getColumnState = (column) => {
  const status = normalizeStatus(column?.change_status);
  if (status === "moved") {
    return "moved";
  }
  if (status === "metadata_changed") {
    return "metadata_changed";
  }
  if (
    ["added", "new", "new_parameter", "new_column", "newly_added"].includes(
      status,
    )
  ) {
    return "added";
  }
  if (
    [
      "removed_retained",
      "removed_parameter_retained",
      "kept_from_old",
      "not_in_new_template",
      "not_available_in_template",
      "retained_from_old",
    ].includes(status)
  ) {
    return "kept";
  }
  if (["removed", "deleted", "not_in_either"].includes(status)) {
    return column?.old_value !== null && column?.old_value !== ""
      ? "kept"
      : "removed";
  }
  return "retained";
};

const displayValue = (value) =>
  value === null || value === undefined || value === ""
    ? "ÃƒÆ’Ã†â€™Ãƒâ€šÃ‚Â¢ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡Ãƒâ€šÃ‚Â¬ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€¦Ã¢â‚¬Å“"
    : String(value);

const getMoveDetails = (column) => {
  if (column?.old_sheet_name && column?.new_sheet_name) {
    return { from: column.old_sheet_name, to: column.new_sheet_name };
  }
  const match = String(column?.change_details || "").match(
    /^Moved from (.+?) to (.+?)[.]?$/i,
  );
  return match ? { from: match[1].trim(), to: match[2].trim() } : null;
};

const getVisibleGroups = (groups, side) => {
  const visibleGroups = [...groups]
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    .map((group) => ({
      ...group,
      columns: [
        ...(group.columns?.length
          ? group.columns
          : [
              {
                column_id: `tab-${group.tab_id}`,
                column_name: "New Tab",
                display_order: 1,
                change_status: group.change_status,
                change_details: "Added by the new template",
              },
            ]),
      ]
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
        .filter((column) => {
          const state = getColumnState(column);
          return side === "old" ? state !== "added" : state !== "removed";
        }),
    }));

  if (side === "old") {
    const movedColumns = groups.flatMap((group) =>
      (group.columns || []).filter(
        (column) => getColumnState(column) === "moved",
      ),
    );

    movedColumns.forEach((column) => {
      const move = getMoveDetails(column);
      if (!move) return;

      visibleGroups.forEach((group) => {
        group.columns = group.columns.filter(
          (item) => item.column_id !== column.column_id,
        );
      });

      const sourceGroup = visibleGroups.find(
        (group) =>
          String(group.tab_name).trim().toLowerCase() ===
          move.from.toLowerCase(),
      );
      if (sourceGroup) {
        sourceGroup.columns.push({
          ...column,
          movedFrom: move.from,
          movedTo: move.to,
        });
      }
    });
  }

  return visibleGroups.filter((group) => group.columns.length > 0);
};
const PreviewTable = ({ title, templateVersion, groups, rows, side }) => {
  const visibleGroups = getVisibleGroups(groups, side);
  const columns = visibleGroups.flatMap((group) => group.columns);

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={1}
        mb={1}
      >
        <Typography fontWeight={700} fontSize={14}>
          {title}
        </Typography>
        <Chip
          size="small"
          color={side === "old" ? "default" : "success"}
          variant="filled"
          label={`${COPY_BATCH_TEMPLATE_VERSION_LABEL} ${templateVersion ?? "-"}`}
          sx={{
            height: 26,
            fontSize: 14,
            fontWeight: 700,
            "& .MuiChip-label": { px: 1 },
          }}
        />
      </Box>
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflowX: "auto",
          px: 0.5,
          boxSizing: "border-box",
        }}
      >
        <Box
          component="table"
          sx={{ width: "100%", minWidth: 480, borderCollapse: "collapse" }}
        >
          <Box component="thead">
            <Box component="tr">
              <Box
                component="th"
                rowSpan={2}
                sx={{ ...cellSx, bgcolor: "#F5F7FA", minWidth: 110 }}
              >
                Parameter
              </Box>
              {visibleGroups.map((group) => {
                return (
                  <Box
                    component="th"
                    key={`${side}-${group.tab_id}`}
                    colSpan={group.columns.length}
                    sx={{
                      ...cellSx,
                      bgcolor: "#F5F7FA",
                      color: "#101828",
                    }}
                  >
                    {group.tab_name}
                  </Box>
                );
              })}
            </Box>
            <Box component="tr">
              {columns.map((column) => {
                const style = stateStyles[getColumnState(column)];
                return (
                  <Tooltip
                    key={`${side}-header-${column.column_id}`}
                    title={side === "new" ? column.change_details || "" : ""}
                    placement="top"
                    arrow
                  >
                    <Box
                      component="th"
                      sx={{
                        ...cellSx,
                        bgcolor: style.background,
                        color: style.color,
                        cursor:
                          side === "new" && column.change_details
                            ? "help"
                            : "default",
                      }}
                    >
                      {side === "new" &&
                      getColumnState(column) === "metadata_changed" ? (
                        <MetadataChangesPopover column={column} />
                      ) : (
                        column.column_name
                      )}
                      {/* {getColumnState(column) === "moved" && (
                        <Typography
                          component="span"
                          fontSize={11}
                          fontWeight={700}
                          ml={0.75}
                        >
                          (Moved)
                        </Typography>
                      )} */}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
          <Box component="tbody">
            {[...rows]
              .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
              .map((row) => {
                const valuesByColumn = new Map(
                  (row.values || []).map((value) => [value.column_id, value]),
                );
                return (
                  <Box component="tr" key={`${side}-${row.row_id}`}>
                    <Box component="th" sx={{ ...cellSx, bgcolor: "#F8F9FA" }}>
                      {row.parameter_name || `Row ${row.row_id}`}
                    </Box>
                    {columns.map((column) => {
                      const value = valuesByColumn.get(column.column_id);
                      const state = getColumnState({
                        ...column,
                        change_status:
                          value?.change_status || column.change_status,
                        old_value: value?.old_value,
                      });
                      const style = stateStyles[state];
                      const cellValue =
                        side === "old"
                          ? value?.old_value
                          : value?.preview_value;
                      return (
                        <Box
                          component="td"
                          key={`${side}-${row.row_id}-${column.column_id}`}
                          sx={{ ...cellSx, bgcolor: style.background }}
                        >
                          {displayValue(cellValue)}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const TemplatePreview = ({
  data,
  unitOperationName,
  historicalTemplateVersion,
  latestApprovedTemplateVersion,
}) => {
  const oldGroups =
    data?.old_structure?.column_groups || data?.column_groups || [];
  const newGroups =
    data?.new_structure?.column_groups || data?.column_groups || [];
  const rows = data?.rows || [];
  const templateName = unitOperationName || "Unit Operation";

  if (!oldGroups.length && !newGroups.length) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        No template preview data is available for this batch.
      </Alert>
    );
  }

  return (
    <Box mt={2}>
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        flexDirection="column"
        sx={{ width: "100%", minWidth: 0 }}
      >
        <PreviewTable
          title={`Old ${templateName} Template`}
          templateVersion={historicalTemplateVersion}
          groups={oldGroups}
          rows={rows}
          side="old"
        />
        <ArrowForwardIcon
          aria-label="Copied to new template"
          sx={{
            color: "#A6A8AB",
            fontSize: 38,
            flexShrink: 0,
            transform: "rotate(90deg)",
          }}
        />
        <PreviewTable
          title={`New ${templateName} Template`}
          templateVersion={latestApprovedTemplateVersion}
          groups={newGroups}
          rows={rows}
          side="new"
        />
      </Box>
      <Box
        mt={1.5}
        p={1}
        display="flex"
        justifyContent="center"
        flexWrap="wrap"
        gap={2}
        sx={{ border: "1px solid #DDE3EA", borderRadius: 1 }}
      >
        {legendItems.map(([state, label]) => (
          <Box
            key={state}
            component="span"
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: 1,
              bgcolor: stateStyles[state].background,
              color: stateStyles[state].color,
              border: "1px solid #DDE3EA",
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.4,
            }}
          >
            {label}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
const CopyBatchDialog = ({
  open,
  batchId,
  loading,
  previewLoading,
  previewData,
  unitOperationName,
  historicalTemplateVersion,
  latestApprovedTemplateVersion,
  onClose,
  onConfirm,
}) => {
  const preview = previewData?.data || previewData;

  return (
    <Dialog
    open={open}
    onClose={loading ? undefined : onClose}
    fullWidth
    maxWidth="lg"
    aria-labelledby="copy-batch-dialog-title"
  >
    <DialogTitle
      id="copy-batch-dialog-title"
      sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}
    >
      <InfoOutlinedIcon color="primary" fontSize="small" />
      <Typography component="span" fontSize={16} fontWeight={700}>
        Important: Copy Batch with New Template
      </Typography>
      <IconButton
        aria-label="Close copy batch dialog"
        onClick={onClose}
        disabled={loading}
        sx={{ position: "absolute", right: 12, top: 10 }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>

    <DialogContent
      dividers
      sx={{
        maxHeight: "calc(100vh - 150px)",
        overflowX: "hidden",
        px: { xs: 2, sm: 3 },
      }}
    >
      <Typography color="text.secondary" fontSize={14} mb={1}>
        You are about to copy batch <strong>{batchId}</strong> with a new
        template. Please review the changes that will be applied.
      </Typography>
      <Box display="flex" flexDirection="column" gap={0.5}>
        {messages.map(({ severity, content }, index) => (
          <Alert
            key={index}
            severity={severity}
            variant="standard"
            sx={{ py: 0, bgcolor: "transparent", color: "text.primary" }}
          >
            {content}
          </Alert>
        ))}
      </Box>
      <DerivedParameterChanges changes={preview?.derived_parameter_changes} />
      <Typography fontSize={14} fontWeight={700} mt={2} mb={0.5}>
        Template Comparison
      </Typography>
      {previewLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={1}
          py={6}
        >
          <CircularProgress size={24} />
          <Typography fontSize={14}>Loading template preview...</Typography>
        </Box>
      ) : (
        <TemplatePreview
          data={preview}
          unitOperationName={unitOperationName}
          historicalTemplateVersion={historicalTemplateVersion}
          latestApprovedTemplateVersion={latestApprovedTemplateVersion}
        />
      )}
      {!previewLoading &&
        (previewData?.data?.message || previewData?.message) && (
          <Alert
            severity={
              (previewData?.data?.can_copy ?? previewData?.can_copy) === false
                ? "error"
                : "info"
            }
            sx={{ mt: 1 }}
          >
            {previewData?.data?.message || previewData?.message}
          </Alert>
        )}
    </DialogContent>

    <DialogActions sx={{ px: 3, py: 2 }}>
      <Button
        variant="contained"
        onClick={onConfirm}
        disabled={
          loading ||
          previewLoading ||
          !previewData ||
          (previewData?.data?.can_copy ?? previewData?.can_copy) === false
        }
      >
        {loading ? "Copying..." : "I Understand"}
      </Button>
    </DialogActions>
    </Dialog>
  );
};

export default CopyBatchDialog;
